import { BarChart2, Clock, DollarSign, Star } from "lucide-react";
import React, { useEffect, useState } from "react";

import api from "@/services/api";

interface AnalyticsData {
  averageRating: number;
  completedEarnings: number;
  currency: string;
  insights: Array<{
    amount: number;
    label: string;
    type: string;
    value: string;
  }>;
  monthlyRides: Array<{ month: string; rides: number }>;
  paymentMethods: Array<{
    count: number;
    method: string;
    percentage: number;
    total: number;
  }>;
  pendingEarnings: number;
  ratingsDistribution: Array<{
    count: number;
    percentage: number;
    rating: number;
  }>;
  rideTypes: Array<{ count: number; percentage: number; type: string }>;
  timeDistribution: Array<{ count: number; hour: number }>;
  timeOnline: number;
  totalEarnings: number;
  totalRatings: number;
  totalRevenue: number;
  totalTrips: number;
  weeklyEarnings: Array<{ earnings: number; trips: number; week: string }>;
}

interface AnalyticsProps {
  // Add props here if needed
}

interface ComprehensiveAnalyticsResponse {
  data: {
    insights: Array<{
      amount: number;
      label: string;
      type: string;
      value: string;
    }>;
    lifetime_earnings: {
      completed: number;
      pending: number;
      total: number;
    };
    monthly_rides: Array<{
      month: string;
      rides: number;
    }>;
    payment_methods: Array<{
      count: number;
      method: string;
      percentage: number;
      total: number;
    }>;
    ratings_distribution: Array<{
      count: number;
      percentage: number;
      rating: number;
    }>;
    ride_types: Array<{
      count: number;
      percentage: number;
      type: string;
    }>;
    summary: {
      average_rating: number;
      currency: string;
      time_online: number;
      total_ratings: number;
      total_revenue: number;
      total_trips: number;
    };
    time_distribution: Array<{
      count: number;
      hour: number;
    }>;
    weekly_earnings: Array<{
      earnings: number;
      trips: number;
      week: string;
    }>;
  };
  success: boolean;
}

const Analytics: React.FC<AnalyticsProps> = () => {
  const [timeRange, setTimeRange] = useState<"month" | "week" | "year">("week");
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    averageRating: 4.8,
    completedEarnings: 0,
    currency: "ZAR",
    insights: [],
    monthlyRides: [],
    paymentMethods: [],
    pendingEarnings: 0,
    ratingsDistribution: [],
    rideTypes: [],
    timeDistribution: [],
    timeOnline: 0,
    totalEarnings: 0,
    totalRatings: 0,
    totalRevenue: 0,
    totalTrips: 0,
    weeklyEarnings: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | string>(null);

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        setLoading(true);

        // Fetch comprehensive analytics data from single endpoint
        const response = await api.get<ComprehensiveAnalyticsResponse>(
          `/driver/analytics/comprehensive`,
        );

        if (response.data.success) {
          const data = response.data.data;

          // Transform API data to component state
          setAnalyticsData({
            averageRating: data.summary.average_rating,
            completedEarnings: data.lifetime_earnings.completed,
            currency: data.summary.currency,
            insights: data.insights,
            monthlyRides: data.monthly_rides,
            paymentMethods: data.payment_methods,
            pendingEarnings: data.lifetime_earnings.pending,
            ratingsDistribution: data.ratings_distribution,
            rideTypes: data.ride_types,
            timeDistribution: data.time_distribution,
            timeOnline: data.summary.time_online,
            totalEarnings: data.lifetime_earnings.total,
            totalRatings: data.summary.total_ratings,
            totalRevenue: data.summary.total_revenue,
            totalTrips: data.summary.total_trips,
            weeklyEarnings: data.weekly_earnings,
          });
        }
        setError(null);
      } catch (err) {
        console.error("Error fetching analytics data:", err);
        setError("Failed to load analytics data");
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, []);

  const getPerformanceMetrics = () => {
    return [
      {
        change: "+12%",
        changeType: "positive" as const,
        icon: <BarChart2 className="h-6 w-6" />,
        title: "Total Rides",
        value: analyticsData.totalTrips.toLocaleString(),
      },
      {
        change: "+8.5%",
        changeType: "positive" as const,
        icon: <DollarSign className="h-6 w-6" />,
        title: "Total Earnings",
        value: `${analyticsData.currency} ${analyticsData.totalRevenue.toLocaleString()}`,
      },
      {
        change: "+0.2",
        changeType: "positive" as const,
        icon: <Star className="h-6 w-6" />,
        title: "Average Rating",
        value: analyticsData.averageRating.toFixed(1),
      },
      {
        change: "-2.3%",
        changeType: "negative" as const,
        icon: <Clock className="h-6 w-6" />,
        title: "Hours Online",
        value: `${analyticsData.timeOnline.toFixed(1)}h`,
      },
    ];
  };

  return (
    <div className="min-h-screen p-4 dark:bg-gray-900 sm:p-6 lg:p-8">
      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-4 text-red-800 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-gray-600 dark:border-t-blue-400"></div>
            <p className="text-gray-600 dark:text-gray-400">
              Loading earnings data...
            </p>
          </div>
        </div>
      )}

      {!loading && (
        <div>
          <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
            <div>
              <h1 className="mb-1.5 text-2xl font-bold text-gray-900 dark:text-gray-100 sm:mb-2 sm:text-3xl">
                Analytics
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 sm:text-base">
                Track your performance and earnings over time.
              </p>
            </div>

            {/* Time Range Selector */}
            <div className="flex w-full rounded-lg bg-gray-100 p-1 dark:bg-gray-700 sm:w-auto">
              {(["week", "month", "year"] as const).map((range) => (
                <button
                  className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors sm:flex-none sm:px-4 sm:py-2 sm:text-sm ${
                    timeRange === range
                      ? "bg-white text-blue-600 shadow-sm dark:bg-gray-800 dark:text-blue-400"
                      : "text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
                  }`}
                  key={range}
                  onClick={() => setTimeRange(range)}
                >
                  {range.charAt(0).toUpperCase() + range.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="mb-6 grid grid-cols-1 gap-3 sm:mb-8 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4 lg:gap-6">
            {getPerformanceMetrics().map((metric, index) => (
              <div
                className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-5 lg:p-6"
                key={index}
              >
                <div className="mb-3 flex items-center justify-between sm:mb-4">
                  <div className="rounded-full bg-blue-100 p-2 text-blue-600 dark:bg-blue-900 dark:text-blue-400 sm:p-2.5 lg:p-3">
                    {metric.icon}
                  </div>
                  <span
                    className={`text-xs font-medium sm:text-sm ${
                      metric.changeType === "positive"
                        ? "text-green-600 dark:text-green-400"
                        : "text-red-600 dark:text-red-400"
                    }`}
                  >
                    {metric.change}
                  </span>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">
                    {metric.title}
                  </p>
                  <p className="text-xl font-semibold text-gray-900 dark:text-gray-100 sm:text-2xl">
                    {metric.value}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Lifetime Earnings Summary */}
          <div className="mb-6 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 p-6 shadow-md dark:from-blue-900/20 dark:to-indigo-900/20 sm:mb-8">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              Lifetime Earnings
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-lg bg-white p-4 dark:bg-gray-800">
                <p className="mb-1 text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Earnings
                </p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {analyticsData.currency}{" "}
                  {analyticsData.totalEarnings.toLocaleString()}
                </p>
              </div>
              <div className="rounded-lg bg-white p-4 dark:bg-gray-800">
                <p className="mb-1 text-sm font-medium text-gray-600 dark:text-gray-400">
                  Completed
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {analyticsData.currency}{" "}
                  {analyticsData.completedEarnings.toLocaleString()}
                </p>
              </div>
              <div className="rounded-lg bg-white p-4 dark:bg-gray-800">
                <p className="mb-1 text-sm font-medium text-gray-600 dark:text-gray-400">
                  Pending
                </p>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {analyticsData.currency}{" "}
                  {analyticsData.pendingEarnings.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Pie Charts Section */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:mb-8 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4 lg:gap-6">
            {/* Ride Types */}
            <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-5 lg:p-6">
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Ride Types
              </h3>
              <div className="flex flex-col items-center justify-center">
                {analyticsData.rideTypes.length > 0 ? (
                  <>
                    <div className="relative h-32 w-32">
                      <svg
                        className="h-32 w-32 -rotate-90 transform"
                        viewBox="0 0 42 42"
                      >
                        {analyticsData.rideTypes.map((ride, idx) => {
                          const offset = analyticsData.rideTypes
                            .slice(0, idx)
                            .reduce((sum, r) => sum + r.percentage, 0);
                          const colors = ["#3B82F6", "#10B981", "#F59E0B"];
                          return (
                            <circle
                              cx="21"
                              cy="21"
                              fill="transparent"
                              key={idx}
                              r="15.915"
                              stroke={colors[idx % colors.length]}
                              strokeDasharray={`${ride.percentage} ${100 - ride.percentage}`}
                              strokeDashoffset={25 - (offset * 100) / 100}
                              strokeWidth="3"
                            />
                          );
                        })}
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {analyticsData.totalTrips}
                        </span>
                      </div>
                    </div>
                    <div className="mt-4 w-full space-y-2">
                      {analyticsData.rideTypes.map((ride, idx) => {
                        const colors = [
                          "bg-blue-500",
                          "bg-green-500",
                          "bg-yellow-500",
                        ];
                        return (
                          <div
                            className="flex items-center justify-between"
                            key={idx}
                          >
                            <div className="flex items-center">
                              <div
                                className={`mr-2 h-3 w-3 rounded-full ${colors[idx % colors.length]}`}
                              ></div>
                              <span className="text-sm capitalize text-gray-600 dark:text-gray-400">
                                {ride.type}
                              </span>
                            </div>
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {ride.percentage.toFixed(1)}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No data available
                  </p>
                )}
              </div>
            </div>

            {/* Time Distribution */}
            <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-5 lg:p-6">
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Time Distribution
              </h3>
              <div className="flex flex-col items-center justify-center">
                {analyticsData.timeDistribution.length > 0 ? (
                  <>
                    <div className="relative h-32 w-32">
                      <svg
                        className="h-32 w-32 -rotate-90 transform"
                        viewBox="0 0 42 42"
                      >
                        {analyticsData.timeDistribution.map((time, idx) => {
                          const total = analyticsData.timeDistribution.reduce(
                            (s, t) => s + t.count,
                            0,
                          );
                          const percentage = (time.count / total) * 100;
                          const offset = analyticsData.timeDistribution
                            .slice(0, idx)
                            .reduce(
                              (sum, t) => sum + (t.count / total) * 100,
                              0,
                            );
                          const colors = [
                            "#F97316",
                            "#EAB308",
                            "#8B5CF6",
                            "#1F2937",
                          ];
                          return (
                            <circle
                              cx="21"
                              cy="21"
                              fill="transparent"
                              key={idx}
                              r="15.915"
                              stroke={colors[idx % colors.length]}
                              strokeDasharray={`${percentage} ${100 - percentage}`}
                              strokeDashoffset={25 - (offset * 100) / 100}
                              strokeWidth="3"
                            />
                          );
                        })}
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {analyticsData.timeDistribution.reduce(
                            (s, t) => s + t.count,
                            0,
                          )}{" "}
                          hrs
                        </span>
                      </div>
                    </div>
                    <div className="mt-4 w-full space-y-2">
                      {analyticsData.timeDistribution
                        .slice(0, 4)
                        .map((time, idx) => {
                          const colors = [
                            "bg-orange-500",
                            "bg-yellow-500",
                            "bg-purple-500",
                            "bg-gray-800 dark:bg-gray-600",
                          ];
                          return (
                            <div
                              className="flex items-center justify-between"
                              key={idx}
                            >
                              <div className="flex items-center">
                                <div
                                  className={`mr-2 h-3 w-3 rounded-full ${colors[idx % colors.length]}`}
                                ></div>
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                  {time.hour}:00
                                </span>
                              </div>
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {time.count}
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No data available
                  </p>
                )}
              </div>
            </div>

            {/* Payment Methods */}
            <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-5 lg:p-6">
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Payment Methods
              </h3>
              <div className="flex flex-col items-center justify-center">
                {analyticsData.paymentMethods.length > 0 ? (
                  <>
                    <div className="relative h-32 w-32">
                      <svg
                        className="h-32 w-32 -rotate-90 transform"
                        viewBox="0 0 42 42"
                      >
                        {analyticsData.paymentMethods.map((method, idx) => {
                          const offset = analyticsData.paymentMethods
                            .slice(0, idx)
                            .reduce((sum, m) => sum + m.percentage, 0);
                          const colors = ["#06B6D4", "#84CC16", "#EC4899"];
                          return (
                            <circle
                              cx="21"
                              cy="21"
                              fill="transparent"
                              key={idx}
                              r="15.915"
                              stroke={colors[idx % colors.length]}
                              strokeDasharray={`${method.percentage} ${100 - method.percentage}`}
                              strokeDashoffset={25 - (offset * 100) / 100}
                              strokeWidth="3"
                            />
                          );
                        })}
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {analyticsData.currency}{" "}
                          {analyticsData.totalRevenue.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="mt-4 w-full space-y-2">
                      {analyticsData.paymentMethods.map((method, idx) => {
                        const colors = [
                          "bg-cyan-500",
                          "bg-lime-500",
                          "bg-pink-500",
                        ];
                        return (
                          <div
                            className="flex items-center justify-between"
                            key={idx}
                          >
                            <div className="flex items-center">
                              <div
                                className={`mr-2 h-3 w-3 rounded-full ${colors[idx % colors.length]}`}
                              ></div>
                              <span className="text-sm capitalize text-gray-600 dark:text-gray-400">
                                {method.method}
                              </span>
                            </div>
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {method.percentage.toFixed(1)}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No data available
                  </p>
                )}
              </div>
            </div>

            {/* Customer Ratings */}
            <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-5 lg:p-6">
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Customer Ratings
              </h3>
              <div className="flex flex-col items-center justify-center">
                {analyticsData.ratingsDistribution.length > 0 ? (
                  <>
                    <div className="relative h-32 w-32">
                      <svg
                        className="h-32 w-32 -rotate-90 transform"
                        viewBox="0 0 42 42"
                      >
                        {analyticsData.ratingsDistribution.map(
                          (rating, idx) => {
                            const offset = analyticsData.ratingsDistribution
                              .slice(0, idx)
                              .reduce((sum, r) => sum + r.percentage, 0);
                            const colors = [
                              "#059669",
                              "#0891B2",
                              "#D97706",
                              "#DC2626",
                            ];
                            return (
                              <circle
                                cx="21"
                                cy="21"
                                fill="transparent"
                                key={idx}
                                r="15.915"
                                stroke={colors[idx % colors.length]}
                                strokeDasharray={`${rating.percentage} ${100 - rating.percentage}`}
                                strokeDashoffset={25 - (offset * 100) / 100}
                                strokeWidth="3"
                              />
                            );
                          },
                        )}
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {analyticsData.averageRating}★
                        </span>
                      </div>
                    </div>
                    <div className="mt-4 w-full space-y-2">
                      {analyticsData.ratingsDistribution.map((rating, idx) => {
                        const colors = [
                          "bg-emerald-600",
                          "bg-cyan-600",
                          "bg-amber-600",
                          "bg-red-600",
                        ];
                        return (
                          <div
                            className="flex items-center justify-between"
                            key={idx}
                          >
                            <div className="flex items-center">
                              <div
                                className={`mr-2 h-3 w-3 rounded-full ${colors[idx % colors.length]}`}
                              ></div>
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {rating.rating} Stars
                              </span>
                            </div>
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {rating.percentage.toFixed(1)}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No data available
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:mb-8 sm:gap-6 lg:grid-cols-2 lg:gap-8">
            {/* Earnings Line Chart */}
            <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-5 lg:p-6">
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Weekly Earnings
              </h3>
              <div className="relative h-48 sm:h-56 lg:h-64">
                {analyticsData.weeklyEarnings.length > 0 ? (
                  <svg
                    className="h-full w-full"
                    preserveAspectRatio="xMidYMid meet"
                    viewBox="0 0 420 220"
                  >
                    {/* Grid lines */}
                    {[0, 1, 2, 3, 4].map((i) => (
                      <line
                        key={i}
                        stroke="#E5E7EB"
                        strokeDasharray="4"
                        strokeWidth="1"
                        x1="40"
                        x2="400"
                        y1={40 + i * 35}
                        y2={40 + i * 35}
                      />
                    ))}

                    {/* Line chart */}
                    <polyline
                      fill="none"
                      points={analyticsData.weeklyEarnings
                        .map((item, index) => {
                          const x =
                            60 +
                            (index * 280) /
                              (analyticsData.weeklyEarnings.length - 1);
                          const maxEarning = Math.max(
                            ...analyticsData.weeklyEarnings.map(
                              (e) => e.earnings,
                            ),
                          );
                          const y = 180 - (item.earnings / maxEarning) * 140;
                          return `${x},${y}`;
                        })
                        .join(" ")}
                      stroke="#3B82F6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="3"
                    />

                    {/* Data points and labels */}
                    {analyticsData.weeklyEarnings.map((item, index) => {
                      const x =
                        60 +
                        (index * 280) /
                          (analyticsData.weeklyEarnings.length - 1);
                      const maxEarning = Math.max(
                        ...analyticsData.weeklyEarnings.map((e) => e.earnings),
                      );
                      const y = 180 - (item.earnings / maxEarning) * 140;
                      return (
                        <g key={index}>
                          <circle cx={x} cy={y} fill="#3B82F6" r="4" />
                          {/* Value labels above points */}
                          <text
                            className="fill-gray-700 text-[10px] font-medium dark:fill-gray-300"
                            textAnchor="middle"
                            x={x}
                            y={y - 10}
                          >
                            R{item.earnings}
                          </text>
                          {/* Week labels */}
                          <text
                            className="fill-gray-500 text-[10px] dark:fill-gray-400"
                            textAnchor="middle"
                            x={x}
                            y="205"
                          >
                            {item.week}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No data available
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Rides Bar Chart */}
            <div className="rounded-lg bg-white p-4 shadow-md dark:bg-gray-800 sm:p-5 lg:p-6">
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Monthly Rides
              </h3>
              <div className="relative h-48 sm:h-56 lg:h-64">
                {analyticsData.monthlyRides.length > 0 ? (
                  <svg
                    className="h-full w-full"
                    preserveAspectRatio="xMidYMid meet"
                    viewBox="0 0 420 220"
                  >
                    {/* Grid lines */}
                    {[0, 1, 2, 3, 4].map((i) => (
                      <line
                        key={i}
                        stroke="#E5E7EB"
                        strokeDasharray="4"
                        strokeWidth="1"
                        x1="40"
                        x2="400"
                        y1={40 + i * 35}
                        y2={40 + i * 35}
                      />
                    ))}

                    {/* Line chart */}
                    <polyline
                      fill="none"
                      points={analyticsData.monthlyRides
                        .map((item, index) => {
                          const x =
                            50 +
                            (index * 320) /
                              (analyticsData.monthlyRides.length - 1);
                          const maxRides = Math.max(
                            ...analyticsData.monthlyRides.map((m) => m.rides),
                          );
                          const y = 180 - (item.rides / maxRides) * 140;
                          return `${x},${y}`;
                        })
                        .join(" ")}
                      stroke="#10B981"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="3"
                    />

                    {/* Data points and labels */}
                    {analyticsData.monthlyRides.map((item, index) => {
                      const x =
                        50 +
                        (index * 320) / (analyticsData.monthlyRides.length - 1);
                      const maxRides = Math.max(
                        ...analyticsData.monthlyRides.map((m) => m.rides),
                      );
                      const y = 180 - (item.rides / maxRides) * 140;
                      return (
                        <g key={index}>
                          <circle cx={x} cy={y} fill="#10B981" r="3" />
                          {/* Value labels above points (every 2 months) */}
                          {index % 2 === 0 && (
                            <text
                              className="fill-gray-700 text-[10px] font-medium dark:fill-gray-300"
                              textAnchor="middle"
                              x={x}
                              y={y - 10}
                            >
                              {item.rides}
                            </text>
                          )}
                          {/* Month labels (every 2 months) */}
                          {index % 2 === 0 && (
                            <text
                              className="fill-gray-500 text-[10px] dark:fill-gray-400"
                              textAnchor="middle"
                              x={x}
                              y="205"
                            >
                              {item.month}
                            </text>
                          )}
                        </g>
                      );
                    })}
                  </svg>
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No data available
                    </p>
                  </div>
                )}
              </div>
              {/* Summary */}
              <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 sm:mt-3">
                Total rides this year:{" "}
                {analyticsData.monthlyRides.reduce((a, b) => a + b.rides, 0)}
              </div>
            </div>
          </div>

          {/* Performance Insights */}
          <div className="rounded-lg bg-white p-6 shadow-md dark:bg-gray-800">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              Performance Insights
            </h3>
            {analyticsData.insights.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {analyticsData.insights.map((insight, idx) => {
                  const bgColorClass =
                    idx % 2 === 0
                      ? "bg-green-50 dark:bg-green-900/20"
                      : "bg-blue-50 dark:bg-blue-900/20";
                  const textColorClass =
                    idx % 2 === 0
                      ? "text-green-600 dark:text-green-400"
                      : "text-blue-600 dark:text-blue-400";
                  const textColorClass2 =
                    idx % 2 === 0
                      ? "text-green-900 dark:text-green-400"
                      : "text-blue-900 dark:text-blue-400";
                  const textColorClass3 =
                    idx % 2 === 0
                      ? "text-green-800 dark:text-green-300"
                      : "text-blue-800 dark:text-blue-300";

                  return (
                    <div className={`rounded-lg p-4 ${bgColorClass}`} key={idx}>
                      <div className="mb-2 flex items-center">
                        <svg
                          className={`mr-2 h-5 w-5 ${textColorClass}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                          />
                        </svg>
                        <h4 className={`font-medium ${textColorClass2}`}>
                          {insight.label}
                        </h4>
                      </div>
                      <p className={`text-sm ${textColorClass3}`}>
                        {insight.value}
                        {insight.amount > 0 &&
                          ` - ${analyticsData.currency} ${insight.amount.toLocaleString()}`}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-8 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No insights available yet
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
